<template>
  <div>
    <template v-if="!isMobile">
      <table
        class="w-full border-separate border-spacing-0"
      >
        <thead class="bg-background sticky top-0">
          <tr>
            <template
              v-for="column in columns"
              :key="column.id || column.header"
            >
              <th
                :class="[
                  'border-b-1 border-background-fg/8 p-2',
                  `text-${column.align || 'left'}`,
                  column.isSortable ? 'cursor-pointer' : '',
                ]"
                @click="onClickHeader(column)"
              >
                <div class="flex items-center space-x-2">
                  <span
                    class="text-background-fg/70 whitespace-nowrap text-left text-sm font-normal"
                  >
                    {{ column.header || "" }}
                  </span>
                  <template v-if="column.isSortable">
                    <template
                      v-if="sortModel === column.id"
                    >
                      <IconHeroiconsSolidArrowUp
                        class="h-3 w-3"
                      />
                    </template>
                    <template
                      v-else-if="
                        sortModel === `-${column.id}`
                      "
                    >
                      <IconHeroiconsSolidArrowDown
                        class="h-3 w-3"
                      />
                    </template>
                    <template v-else>
                      <div class="h-3 w-3" />
                    </template>
                  </template>
                </div>
              </th>
            </template>
            <th />
          </tr>
        </thead>
        <tbody class="relative">
          <BaseInnerLoading v-if="isLoading" />
          <template
            v-for="(row, rowIndex) in rows"
            :key="row.index"
          >
            <tr
              :class="getRowClass ? getRowClass(row) : ''"
            >
              <template
                v-for="column in columns"
                :key="column.id || column.header"
              >
                <td
                  :class="[
                    `border-1-b border-background-fg/5 border-b px-3 py-2 text-sm font-medium`,
                    column.autoWidth
                      ? 'w-1 whitespace-nowrap'
                      : '',
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'left'
                        ? 'text-left'
                        : '',
                    column.cellClass,
                  ]"
                >
                  <slot
                    :name="`cell-${column.id}`"
                    v-bind="{
                      row,
                      rowIndex,
                      value:
                        column.getCellValue &&
                        column.getCellValue(row),
                    }"
                  >
                    {{
                      column.getCellValue &&
                      column.getCellValue(row)
                    }}
                  </slot>
                </td>
              </template>
            </tr>
          </template>
          <tr v-if="rows.length === 0">
            <td colspan="100%" class="text-center">
              <span v-if="isLoading">&nbsp;</span>
              <span v-else>{{
                noRowsText ?? "No Results"
              }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <template v-else>
      <ul class="relative space-y-2 overflow-y-auto p-1">
        <BaseInnerLoading v-if="isLoading" />
        <template
          v-for="(row, rowIndex) in rows"
          :key="row.id"
        >
          <li>
            <slot
              name="grid-card"
              v-bind="{ row, rowIndex }"
            >
              <dl class="space-y-2 p-2 shadow">
                <template
                  v-for="column in columns"
                  :key="column.id || column.header"
                >
                  <div
                    v-if="!column.desktopOnly"
                    class="flex items-center justify-between"
                  >
                    <dt class="text-xs">
                      {{ column.header }}
                    </dt>
                    <dd
                      class="text-background-fg/87 text-sm font-semibold"
                    >
                      <slot
                        :name="`cell-${column.id}`"
                        v-bind="{
                          row,
                          rowIndex,
                          value:
                            column.getCellValue &&
                            column.getCellValue(row),
                        }"
                      >
                        {{
                          column.getCellValue &&
                          column.getCellValue(row)
                        }}
                      </slot>
                    </dd>
                  </div>
                </template>
                <slot
                  name="grid-card-footer"
                  v-bind="{ row, rowIndex }"
                />
              </dl>
            </slot>
          </li>
        </template>
        <li v-if="rows.length === 0">
          <div class="p-2 text-center shadow">
            <span v-if="isLoading">&nbsp;</span>
            <span v-else>{{
              noRowsText ?? "No Results"
            }}</span>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts" generic="TRow extends {}">
export type TableColumn<T> = {
  id: string
  header?: string
  getCellValue?: (row: T) => any
  autoWidth?: boolean
  align?: "right" | "left" | "center"
  isSortable?: boolean
  desktopOnly?: boolean
  cellClass?: string
}

const props = defineProps<{
  columns: Array<TableColumn<TRow>>
  rows: Array<TRow> | ReadonlyArray<TRow>
  ordering?: string
  isLoading?: boolean
  noRowsText?: string
  getRowClass?: (row: TRow) => string
}>()

const emit = defineEmits<{
  (
    event: "update:ordering",
    value: string | undefined
  ): void
}>()

const isMobile = useIsMobile()

const sortModel = computed({
  get: () => props.ordering,
  set: (value) => emit("update:ordering", value),
})

const onClickHeader = (column: TableColumn<TRow>) => {
  if (!column.isSortable) return

  if (sortModel.value === column.id) {
    sortModel.value = `-${column.id}`
  } else if (sortModel.value === `-${column.id}`) {
    sortModel.value = undefined
  } else {
    sortModel.value = column.id
  }
}
</script>
